import DoubleArrow from '@mui/icons-material/DoubleArrow';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  AppBar,
  Box,
  Checkbox,
  IconButton,
  LinearProgress,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { useContext } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import useToggle from '../../../../hooks/useToggle';
import UserContext from '../../../../shared-components/contexts/UserContext';
import NoItemBox from '../../../../shared-components/NoItemBox';
import { AlertStatus } from '../../../../shared-types/Notation/AlertStatus';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import { INotationManagementContentPanel } from '../Interfaces/INotationManagementContentPanel';
import {
  NotationManagementDispatch,
  NotationManagementState,
} from '../NotationManagement';
import { NotationManagementActionType } from '../types/NotationManagementActionType';
import ExportDownloadContainer from './ExportDownloadContainer';
import MassLowerAlert from './MassLowerAlert';
import NotationListItem, { NotationListItemSkeleton } from './NotationListItem';

const useStyles = makeStyles(theme => ({
  heading: {
    backgroundColor: '#f9f9f9',
    padding: '10px',
    fontSize: '1.25rem',
    fontWeight: 500,
    margin: '0',
  },
  header: {
    top: '85px', // 85
    background: '#fff',
    zIndex: 998,
    borderBottom: '1px solid #dcdcdc',
    borderTop: '1px solid #dcdcdc',
    [theme.breakpoints.down('md')]: {
      top: '120px',
    },
  },
  chip: {
    marginTop: '5px',
    padding: '1px',
  },
  countText: { color: '#6f6f6f', padding: '8px' },
}));

const NotationManagementContentPanel = (
  props: INotationManagementContentPanel,
) => {
  const classes = useStyles();

  const dispatch = useContext(NotationManagementDispatch);
  const state = useContext(NotationManagementState);
  const user = useContext(UserContext);

  const [openExportContainer, toggleExportContainer] = useToggle();
  const [openMassLowerDialog, toggleMassLowerDialog] = useToggle();

  const disableBulkLower = React.useMemo(() => {
    if (!state.selectAll && state.selectedNotations.length === 0) return true;
    if (state.selectAll) {
      return !(
        state.filters.notationTypes.length === 1 &&
        state.filters.notationTypes.includes(NotationType.Alert) &&
        state.filters.alertStatus.length === 1 &&
        state.filters.alertStatus.includes(AlertStatus.Open)
      );
    } else if (!state.selectAll) {
      return (
        state.selectedNotations.findIndex(
          n => n.notationType !== NotationType.Alert,
        ) > -1 ||
        state.selectedNotations.findIndex(n => n.alertStatus !== 'Open') > -1
      );
    }
    return true;
  }, [state.selectAll, state.selectedNotations]);

  const ToolbarButton = (props: {
    disabled?: boolean;
    disabledText?: string;
    text: string;
    children: React.ReactNode;
    onClick: () => void;
  }) => {
    return (
      <Tooltip
        title={props.disabled ? props.disabledText ?? '' : props.text}
        placement="top"
        arrow
      >
        <span>
          <IconButton
            onClick={props.onClick}
            disabled={props.disabled}
            aria-label={props.text}
            size="small"
          >
            {props.children}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <>
      <Paper elevation={2} square style={{ paddingBottom: '1px', margin: 0 }}>
        <Typography variant="h2" className={classes.heading} component="h2">
          Notations
        </Typography>
        <AppBar position="sticky" className={classes.header} elevation={0}>
          <Toolbar variant="dense" style={{ padding: '0 0 0 30px' }}>
            <Box display="flex" style={{ width: '100%' }}>
              <Box flexGrow={1}>
                <Checkbox
                  color="primary"
                  inputProps={{ 'aria-label': 'select all' }}
                  edge="start"
                  disabled={state.isNotationsLoading}
                  indeterminate={
                    state.selectedNotations.length !== 0 ||
                    state.excludedNotations.length !== 0
                  }
                  checked={
                    state.selectAll || state.selectedNotations.length !== 0
                  }
                  onClick={() => {
                    if (
                      state.selectAll ||
                      state.selectedNotations.length !== 0
                    ) {
                      // deselect all
                      dispatch({
                        type: NotationManagementActionType.DeselectAll,
                      });
                    } else {
                      // select all
                      dispatch({
                        type: NotationManagementActionType.SelectAll,
                      });
                    }
                  }}
                />

                {user.isAdministrator && (
                  <ToolbarButton
                    disabled={disableBulkLower}
                    text="Lower Selected Alerts"
                    disabledText="Only Open Alerts can be Lowered. Please change your selection to include only Open Alerts"
                    onClick={toggleMassLowerDialog}
                  >
                    <DoubleArrow style={{ transform: 'rotate(90deg)' }} />
                  </ToolbarButton>
                )}

                <ToolbarButton
                  text={`${
                    openExportContainer ? 'Hide' : 'Show'
                  } Notation Export`}
                  onClick={toggleExportContainer}
                >
                  <GetAppIcon />
                </ToolbarButton>
              </Box>
              <Box>
                <Typography aria-live="assertive" className={classes.countText}>
                  {`Showing ${state.notations.length.toLocaleString()} of ${state.totalNotations.toLocaleString()}`}
                </Typography>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <div>
          <ExportDownloadContainer
            showExportDownloadContainer={openExportContainer}
          />
        </div>
        <>
          {state.notations.length === 0 &&
            state.isNotationsLoading &&
            [...Array(50)].map((_e, i) => <NotationListItemSkeleton key={i} />)}
          {state.notations.length === 0 && !state.isNotationsLoading && (
            <NoItemBox
              height={575}
              minHeight={497}
              text="No Notations to Display"
            />
          )}
          {state.notations.length > 0 && (
            <InfiniteScroll
              element="ul"
              loadMore={props.getContent}
              initialLoad={false}
              hasMore={
                state.totalNotations > state.notations.length &&
                !state.isNotationsLoading
              }
              useWindow={true}
              style={{
                margin: 0,
                padding: 0,
                position: 'relative',
                listStyle: 'none',
              }}
            >
              {state.notations.map(n => (
                <NotationListItem
                  key={n.id}
                  notation={n}
                  isSelected={
                    (state.selectAll &&
                      state.excludedNotations.findIndex(y => n.id === y.id) ===
                        -1) ||
                    (!state.selectAll &&
                      state.selectedNotations.findIndex(y => n.id === y.id) !==
                        -1)
                  }
                  onDelete={() => {
                    dispatch({
                      type: NotationManagementActionType.DeleteNotation,
                      notationType: n.notationType,
                      id: n.id,
                    });
                  }}
                />
              ))}
            </InfiniteScroll>
          )}
          {state.isNotationsLoading &&
            state.notations.length > 0 &&
            state.totalNotations > state.notations.length && (
              <div className="loading-bar-container">
                <LinearProgress />
              </div>
            )}
        </>
      </Paper>
      <MassLowerAlert
        open={openMassLowerDialog}
        close={toggleMassLowerDialog}
      />
    </>
  );
};

export default NotationManagementContentPanel;
